import { Allow, parse as parsePartialJSON } from 'partial-json';

export type A2UIPreviewRendererInput = {
  surfaceId: string;
  surfaceVersion: number;
  originMessageId: string;
  messages?: Record<string, unknown>[];
  canvasPayload?: Record<string, unknown>;
};

type ParsedPreviewCode = { value: unknown; error: string | null; partial: boolean };

// The preview intentionally accepts structurally incomplete JSON while the model is still streaming.
// We still reject half-written strings so the live chat renderer does not flicker on broken labels/content.
const PARTIAL_A2UI_PREVIEW_ALLOW = Allow.ALL & ~Allow.STR;
const PLAYBACK_LINE_PATTERN = /[^\n]*\n|[^\n]+$/g;
const FORMATTER_INDENT = '  ';
const INLINE_ITEM_ARRAY_KEYS = new Set(['components', 'contents']);

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
}

function asMessages(value: unknown): Record<string, unknown>[] | undefined {
  return Array.isArray(value) ? value.filter((item): item is Record<string, unknown> => Boolean(asRecord(item))) : undefined;
}

function asRenderablePayload(value: unknown): Record<string, unknown> | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  if (asMessages(record.messages) || asMessages(record.objects)) return record;
  return undefined;
}

function normalizeA2UIValue(value: unknown): Record<string, unknown> | undefined {
  const messages = asMessages(value);
  if (messages) return { objects: messages };
  return asRenderablePayload(value);
}

function collectReferencedComponentIds(value: unknown, knownIds: Set<string>, found = new Set<string>()) {
  if (typeof value === 'string') {
    if (knownIds.has(value)) found.add(value);
    return found;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectReferencedComponentIds(item, knownIds, found);
    return found;
  }
  const record = asRecord(value);
  if (!record) return found;
  for (const nestedValue of Object.values(record)) collectReferencedComponentIds(nestedValue, knownIds, found);
  return found;
}

function inferSyntheticRootId(components: unknown[]) {
  const componentRecords = components.filter((component): component is Record<string, unknown> => Boolean(asRecord(component)));
  const componentIds = componentRecords.map((component) => (typeof component.id === 'string' ? component.id : null)).filter((id): id is string => Boolean(id));
  if (!componentIds.length) return undefined;
  const referencedIds = new Set<string>();
  const knownIds = new Set(componentIds);
  for (const component of componentRecords) collectReferencedComponentIds(component.component, knownIds, referencedIds);
  return componentIds.find((id) => !referencedIds.has(id)) ?? componentIds[0];
}

function synthesizeBeginRenderingMessage(messages: Record<string, unknown>[] | undefined, fallbackSurfaceId?: string) {
  if (!messages?.length) return messages;
  if (messages.some((item) => Boolean(asRecord(item.beginRendering)))) return messages;
  for (const item of messages) {
    const surfaceUpdate = asRecord(item.surfaceUpdate);
    const components = Array.isArray(surfaceUpdate?.components) ? surfaceUpdate.components : [];
    const rootId = inferSyntheticRootId(components);
    const surfaceId = typeof surfaceUpdate?.surfaceId === 'string' ? surfaceUpdate.surfaceId : fallbackSurfaceId;
    if (!rootId || !surfaceId) continue;
    return [{ beginRendering: { root: rootId, surfaceId } }, ...messages];
  }
  return messages;
}

function deriveRendererInputFromValue(value: unknown): A2UIPreviewRendererInput | null {
  const record = asRecord(value);
  if (Array.isArray(value)) {
    const normalizedMessages = synthesizeBeginRenderingMessage(asMessages(value), 'a2ui-preview-surface');
    return { surfaceId: 'a2ui-preview-surface', surfaceVersion: 1, originMessageId: 'a2ui-preview-origin', messages: normalizedMessages };
  }
  if (!record) return null;
  const nestedSurface = asRecord(record.canvas_surface);
  const payloadOwner = nestedSurface || record;
  const nestedPayload = asRecord(nestedSurface?.payload);
  const ownPayload = asRecord(record.payload);
  const ownA2UI = normalizeA2UIValue(record.a2ui);
  const directPayload = asRenderablePayload(record);
  const canvasPayload = nestedPayload || ownPayload || ownA2UI || directPayload;
  const surfaceId = typeof payloadOwner.surface_id === 'string' ? payloadOwner.surface_id : typeof record.surface_id === 'string' ? record.surface_id : 'a2ui-preview-surface';
  const surfaceVersion = typeof payloadOwner.surface_version === 'number' ? payloadOwner.surface_version : typeof record.surface_version === 'number' ? record.surface_version : 1;
  const originMessageId = typeof record.origin_message_id === 'string' ? record.origin_message_id : 'a2ui-preview-origin';
  const rawMessages =
    asMessages(record.a2ui_messages) ||
    asMessages(record.a2ui) ||
    asMessages(record.messages) ||
    asMessages(record.objects) ||
    asMessages(nestedSurface?.a2ui_messages) ||
    asMessages(nestedPayload?.messages) ||
    asMessages(nestedPayload?.objects) ||
    asMessages(ownPayload?.messages) ||
    asMessages(ownPayload?.objects) ||
    asMessages(ownA2UI?.messages) ||
    asMessages(ownA2UI?.objects);
  const messages = synthesizeBeginRenderingMessage(rawMessages, surfaceId);
  const normalizedCanvasPayload = canvasPayload && Array.isArray(canvasPayload.objects) ? { ...canvasPayload, objects: synthesizeBeginRenderingMessage(asMessages(canvasPayload.objects), surfaceId) } : canvasPayload;
  return { surfaceId, surfaceVersion, originMessageId, messages, canvasPayload: normalizedCanvasPayload };
}

function unwrapTopLevelPreviewOutput(value: unknown): unknown {
  const record = asRecord(value);
  if (!record || !('output' in record)) return value;
  const derived = deriveRendererInputFromValue(record.output);
  return derived?.messages?.length || derived?.canvasPayload ? record.output : value;
}

export function deriveA2UIPreviewRendererInput(value: unknown): A2UIPreviewRendererInput | null {
  return deriveRendererInputFromValue(unwrapTopLevelPreviewOutput(value));
}

export function parseA2UIPreviewCode(code: string, invalidMessage = 'Invalid JSON'): ParsedPreviewCode {
  const trimmed = code.trim();
  if (!trimmed) return { value: null, error: null, partial: false };
  try {
    return { value: JSON.parse(code), error: null, partial: false };
  } catch (completeError) {
    try {
      return { value: parsePartialJSON(code, PARTIAL_A2UI_PREVIEW_ALLOW), error: null, partial: true };
    } catch (partialError) {
      const error = partialError instanceof Error ? partialError : completeError;
      return { value: null, error: error instanceof Error ? error.message : invalidMessage, partial: false };
    }
  }
}

export function parseA2UIRawDeltaStream(raw: string, invalidMessage = 'Invalid JSON') {
  const candidates = collectA2UIRawDeltaCandidates(raw);
  for (const candidate of candidates) {
    const parsed = parseA2UIPreviewCode(candidate, invalidMessage);
    const rendererInput = parsed.value ? deriveA2UIPreviewRendererInput(parsed.value) : null;
    if (rendererInput?.messages?.length || rendererInput?.canvasPayload) return { ...parsed, rendererInput };
  }
  const parsed = parseA2UIPreviewCode(raw, invalidMessage);
  return { ...parsed, rendererInput: parsed.value ? deriveA2UIPreviewRendererInput(parsed.value) : null };
}

function extractJsonSubstring(raw: string, startIndex: number) {
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let index = startIndex; index < raw.length; index += 1) {
    const char = raw[index];
    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (char === '\\') {
        escaped = true;
        continue;
      }
      if (char === '"') inString = false;
      continue;
    }
    if (char === '"') {
      inString = true;
      continue;
    }
    if (char === '{' || char === '[') {
      depth += 1;
      continue;
    }
    if (char === '}' || char === ']') {
      depth -= 1;
      if (depth === 0) return raw.slice(startIndex, index + 1);
    }
  }
  return raw.slice(startIndex).trim();
}

function extractLastEmbeddedProgressCandidate(raw: string) {
  const marker = '{"type":"progress"';
  const startIndex = raw.lastIndexOf(marker);
  if (startIndex < 0) return '';
  return extractJsonSubstring(raw, startIndex);
}

function extractScalarField(raw: string, fieldName: string) {
  const stringMatch = raw.match(new RegExp(`"${fieldName}"\\s*:\\s*"((?:\\\\.|[^"\\\\])*)"`, 's'));
  if (stringMatch) return `"${fieldName}":"${stringMatch[1]}"`;
  const numberMatch = raw.match(new RegExp(`"${fieldName}"\\s*:\\s*(-?\\d+(?:\\.\\d+)?)`, 's'));
  if (numberMatch) return `"${fieldName}":${numberMatch[1]}`;
  return '';
}

function extractTopLevelA2UICandidate(raw: string) {
  const a2uiKeyIndex = raw.lastIndexOf('"a2ui"');
  if (a2uiKeyIndex < 0) return '';
  const colonIndex = raw.indexOf(':', a2uiKeyIndex);
  if (colonIndex < 0) return '';
  const a2uiValueStart = raw.slice(colonIndex + 1).search(/[{[]/);
  if (a2uiValueStart < 0) return '';
  const a2uiValue = raw.slice(colonIndex + 1 + a2uiValueStart).trim();
  const fields = [extractScalarField(raw, 'text_response'), extractScalarField(raw, 'surface_id'), extractScalarField(raw, 'surface_version'), extractScalarField(raw, 'origin_message_id')].filter(Boolean);
  const prefix = fields.length ? `${fields.join(',')},` : '';
  return `{${prefix}"a2ui":${a2uiValue}`;
}

function collectA2UIRawDeltaCandidates(raw: string) {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  const lines = raw.match(PLAYBACK_LINE_PATTERN) ?? [];
  const completeRecords: string[] = [];
  let trailingPartial = '';
  let currentRecord = '';
  for (const line of lines) {
    currentRecord += line;
    if (line.endsWith('\n')) {
      const normalized = currentRecord.trim();
      if (normalized) completeRecords.push(normalized);
      currentRecord = '';
      continue;
    }
    trailingPartial = currentRecord.trim();
  }
  if (!trailingPartial && currentRecord.trim()) trailingPartial = currentRecord.trim();
  const ordered = [extractTopLevelA2UICandidate(raw), extractLastEmbeddedProgressCandidate(raw), trailingPartial, ...completeRecords.toReversed(), trimmed].filter(Boolean);
  return [...new Set(ordered)];
}

export function getA2UIPreviewPlaybackChunks(code: string) {
  return code.match(PLAYBACK_LINE_PATTERN) ?? [];
}

function formatA2UIPreviewInlineJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return value.length ? `[ ${value.map((item) => formatA2UIPreviewInlineJson(item)).join(', ')} ]` : '[]';
  const entries = Object.entries(value as Record<string, unknown>);
  return entries.length ? `{ ${entries.map(([key, nestedValue]) => `${JSON.stringify(key)}: ${formatA2UIPreviewInlineJson(nestedValue)}`).join(', ')} }` : '{}';
}

function formatA2UIPreviewJsonValue(value: unknown, depth = 0, parentKey?: string, inline = false): string {
  if (inline) return formatA2UIPreviewInlineJson(value);
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  const indent = FORMATTER_INDENT.repeat(depth);
  const childIndent = FORMATTER_INDENT.repeat(depth + 1);
  if (Array.isArray(value)) {
    if (!value.length) return '[]';
    const inlineItems = INLINE_ITEM_ARRAY_KEYS.has(parentKey ?? '');
    return `[\n${value.map((item) => `${childIndent}${formatA2UIPreviewJsonValue(item, depth + 1, undefined, inlineItems)}`).join(',\n')}\n${indent}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (!entries.length) return '{}';
  return `{\n${entries.map(([key, nestedValue]) => `${childIndent}${JSON.stringify(key)}: ${formatA2UIPreviewJsonValue(nestedValue, depth + 1, key)}`).join(',\n')}\n${indent}}`;
}

export function formatA2UIPreviewJson(value: unknown) {
  return formatA2UIPreviewJsonValue(value);
}

export function getA2UIPreviewObjectsJson(value: unknown) {
  const rendererInput = deriveA2UIPreviewRendererInput(value);
  return rendererInput?.messages ? formatA2UIPreviewJson(rendererInput.messages) : null;
}
